import React, { useState, useEffect } from 'react';
import { cmsNavLinks } from '@/lib/pagePaths';
import { Link } from 'react-router-dom';
import { GraduationCap, Mail, MapPin } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '@/config';
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [cmsNav, setCmsNav] = useState([]);
  const [cmsSettings, setCmsSettings] = useState(null);

  useEffect(() => {
    fetchCmsData();
  }, []);

  const fetchCmsData = async () => {
    try {
      const [navRes, settingsRes] = await Promise.all([
        axios.get(`${API}/cms/pages/navigation`),
        axios.get(`${API}/cms/settings`),
      ]);
      setCmsNav(navRes.data.footer || []);
      setCmsSettings(settingsRes.data);
    } catch (e) {
      // Fallback silently
    }
  };

  const quickLinks = [
    { label: 'Browse Courses', url: '/courses' },
    { label: 'Resident Courses', url: '/courses?type=resident' },
    ...(cmsNav.length > 0
      ? cmsNavLinks(cmsNav)
      : [
          { label: 'About Us', url: '/about' },
          { label: 'For Organisations', url: '/for-organisations' },
        ]),
    { label: 'Contact Us', url: '/contact' },
  ];

  const email = cmsSettings?.contact_email || 'admin@commonimpactelearning.co.uk';
  const footerText = cmsSettings?.footer_text || 'Empowering care professionals with quality training';
  const siteName = cmsSettings?.site_name || 'Common Impact';
  const address = cmsSettings?.contact_address || '71-75 Shelton Street, London, WC2H 9JQ, England';

  return (
    <footer className="bg-[#2a3e5d] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <img
                src="/logo-header.png"
                alt="Common Impact E-Learning"
                className="h-10 w-auto max-w-[220px] object-contain brightness-0 invert"
              />
              <span className="font-semibold text-xl">{siteName}</span>
            </div>
            <p className="text-[#f8fafc]/70 text-sm leading-relaxed max-w-md">
              {footerText}
            </p>
            <div className="flex flex-col gap-2 mt-6 text-sm text-[#f8fafc]/60">
              <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-[#20acf6] transition-colors">
                <Mail className="w-4 h-4" />
                {email}
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {address}
              </span>
              {!cmsSettings?.contact_address && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  United Kingdom
                </span>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm tracking-wide uppercase mb-4 text-[#20acf6]">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.url} className="text-[#f8fafc]/60 hover:text-[#20acf6] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm tracking-wide uppercase mb-4 text-[#02d1d4]">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy-policy" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/cookies-policy" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Cookies Policy
                </Link>
              </li>
              <li>
                <Link to="/accessibility" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Accessibility
                </Link>
              </li>
              <li>
                <Link to="/legal" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Legal
                </Link>
              </li>
              <li>
                <Link to="/certificates/verify" className="text-[#f8fafc]/60 hover:text-[#02d1d4] transition-colors">
                  Verify Certificate
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#f8fafc]/40">
            &copy; {new Date().getFullYear()} {siteName} E-learning. All rights reserved.
          </p>
          <p className="text-xs text-[#f8fafc]/30">
            WCAG 2.1 AA Compliant
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
